import React, { useEffect, useState } from "react";
import AppModal from "../../components/layout/app/AppModal";
import AlertDialog from "../../components/layout/app/AlertDialog";
import { useToggleTop } from "../../components/common/ToggleTop";
import useAlive from "../../hooks/useAlive";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import { changedNothing } from "./remoteUtils";

// The app's remote name dialog in edit mode (app/screens/HashhRemote/RemoteNameModal.js),
// opened from a Universal Remotes card and from a remote's "Edit Remote":
// rename the remote, or delete it from the home after the app's confirmation.
// The IRDB routes answer raw database errors, so failures show fixed messages.
// onRenamed gets the new name.
function EditRemoteDialog({ open, blaster, remote, onClose, onRenamed, onDeleted }) {
  const toast = useToggleTop();
  const alive = useAlive();
  const [name, setName] = useState("");
  // "rename" or "delete" while the API works on it.
  const [busy, setBusy] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(remote?.name ?? "");
    setConfirming(false);
  }, [open, remote]);

  const value = name.trim();
  const close = () => {
    if (!busy) onClose();
  };

  const rename = async (event) => {
    event.preventDefault();
    if (!value || value === remote.name || busy) return;
    setBusy("rename");
    try {
      // The API finds the remote under the blaster's deviceId.
      const result = await UserService.renameRemote(User.getPropertyId(), {
        name: value,
        deviceId: blaster.deviceId,
        remoteId: remote.remoteId,
      });
      if (changedNothing(result)) throw new Error("No remote matched");
    } catch (error) {
      if (!alive.current) return;
      setBusy(null);
      toast("Couldn't rename the remote. Please try again.", "red");
      return;
    }
    if (!alive.current) return;
    setBusy(null);
    toast("Renamed remote successfully!", "green");
    onClose();
    onRenamed?.(value);
  };

  const remove = async () => {
    setConfirming(false);
    setBusy("delete");
    try {
      await UserService.deleteRemote(User.getPropertyId(), remote.remoteId);
    } catch (error) {
      if (!alive.current) return;
      setBusy(null);
      toast("Couldn't delete the remote. Please try again.", "red");
      return;
    }
    if (!alive.current) return;
    setBusy(null);
    toast("Remote deleted successfully!", "green");
    onClose();
    onDeleted?.();
  };

  return (
    <>
      <AppModal
        open={open && !confirming}
        onClose={close}
        title="Edit Remote"
        actions={
          <>
            <button type="button" className="hx-btn is-ghost" onClick={close} disabled={Boolean(busy)}>
              Cancel
            </button>
            <button
              type="submit"
              form="hx-remote-edit-form"
              className="hx-btn"
              disabled={Boolean(busy) || !value || value === remote?.name}
            >
              {busy === "rename" ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <form id="hx-remote-edit-form" className="hx-name-form" onSubmit={rename} noValidate>
          <label className="hx-label" htmlFor="hx-remote-edit-name">
            Remote Name
          </label>
          <input
            id="hx-remote-edit-name"
            className="hx-input"
            value={name}
            maxLength={40}
            autoComplete="off"
            disabled={Boolean(busy)}
            onChange={(event) => setName(event.target.value)}
          />
        </form>
        <button
          type="button"
          className="hx-btn is-ghost is-danger-text hx-remote-delete"
          disabled={Boolean(busy)}
          onClick={() => setConfirming(true)}
        >
          {busy === "delete" ? "Deleting Remote…" : "Delete Remote From Property"}
        </button>
      </AppModal>
      <AlertDialog
        open={open && confirming}
        type="confirmation"
        title="Delete Remote"
        message="Are you sure you want to delete this remote from property?"
        confirmLabel="Delete"
        onClose={() => setConfirming(false)}
        onConfirm={remove}
      />
    </>
  );
}

export default EditRemoteDialog;
